import {useState} from 'react'
import {toast} from 'react-toastify'
import {AtaResumo, IrpResumo} from '../types'
import {alertChip, ALERT_ICONS, brl, ESFERA_LABEL, fmtDate, realcar, seloInfo} from '../ui'
import DicasLoading from './DicasLoading'

interface Props {
    resultados: AtaResumo[]
    origem: string
    termo: string
    loading: boolean
    erro: string | null
    sugestoes: string[]
    /** Busca já respondeu 0 atas, mas as sugestões de termo (IA, assíncronas) ainda
     * não chegaram: a lista mostra "processando" em vez do vazio. */
    sugestoesPendentes: boolean
    irps: IrpResumo[]
    onRefinar: () => void
    onSugestao: (termo: string) => void
    onAbrir: (nc: string) => void
    onAvaliar: (nc: string) => void
}

/** Termos sugeridos pela IA (fluxo do "0 resultados", assíncrono). Aparece no vazio
 * honesto E no aviso de fonte indisponível: sugerir um termo melhor ajuda igual
 * quando o PNCP caiu — o gestor tenta outra palavra em vez de só esperar. */
function SugestoesChips({sugestoes, onSugestao}: {sugestoes: string[]; onSugestao: (t: string) => void}) {
    if (!sugestoes.length) return null
    return (
        <div className="sugestoes">
            <div className="sugestoes__label">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a7 7 0 0 0-4 12.7V17a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-2.3A7 7 0 0 0 12 2Z"/><line x1="9" y1="21" x2="15" y2="21"/></svg>
                Termos sugeridos pela Licito.Guru IA
            </div>
            <div className="sugestoes__chips">
                {sugestoes.map((s) => (
                    <button key={s} className="sugestao-chip" onClick={() => onSugestao(s)}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
                        {s}
                    </button>
                ))}
            </div>
        </div>
    )
}

/** Seção "Participar" — IRPs abertas (entrar na ata antes da licitação). */
function IrpSection({irps, termo}: {irps: IrpResumo[]; termo: string}) {
    if (!irps.length) return null
    return (
        <div className="irp-section">
            <div className="irp-section__head">
                <span className="irp-section__badge">Participar</span>
                <div>
                    <div className="irp-section__title">{irps.length} Intenção(ões) de Registro de Preços aberta(s) para "{termo}"</div>
                    <div className="irp-section__sub">Aqui você <strong>participa da ata desde o início</strong> (sem os limites de 50%/dobro da carona). A manifestação de interesse é feita no Compras.gov.br, dentro do prazo.</div>
                </div>
            </div>
            <div className="irp-list">
                {irps.map((irp) => (
                    <div key={irp.codigo} className="irp-card">
                        <div className="irp-card__main">
                            <div className="irp-card__objeto">{realcar(irp.objeto, termo)}</div>
                            <div className="irp-card__meta">
                                <span>{irp.uasg_nome || `UASG ${irp.uasg_codigo}`}</span>
                                <span className="lg-badge lg-badge--info">IRP {irp.numero_irp}</span>
                                {irp.uf && <span style={{color: 'var(--text-tertiary)'}}>· {irp.uf}</span>}
                            </div>
                        </div>
                        <div className="irp-card__side">
                            <div className="irp-card__data">
                                <span className="irp-card__data-label">Licitação prevista</span>
                                <strong>{fmtDate(irp.data_provavel)}</strong>
                            </div>
                            <a className="lg-btn lg-btn--brand lg-btn--sm" href={irp.link} target="_blank" rel="noopener noreferrer">
                                Participar no Compras.gov
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                            </a>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    )
}

type Ordenar = 'aderencia' | 'vigencia'
type Filtro = 'todas' | 'ADESAO_PERMITIDA' | 'ADESAO_CONDICIONAL'

function copiar(nc: string) {
    try {
        navigator.clipboard?.writeText(nc)
        toast.info('Nº de controle PNCP copiado.')
    } catch {
        /* ignore */
    }
}

function AtaCard({ata, termo, onAbrir, onAvaliar}: {ata: AtaResumo; termo: string; onAbrir: (nc: string) => void; onAvaliar: (nc: string) => void}) {
    const selo = seloInfo(ata.selo, ata.selo_rotulo)
    const vedada = selo.key === 'vedada'
    const vencida = selo.key === 'vencida'
    const podeAvaliar = !vedada && !vencida
    const score = Math.round((ata.score || 0) * 100)
    return (
        <div className={`ata-card${vedada ? ' ata-card--vedada' : ''}`}>
            <div className="ata-card__top">
                <div>
                    <div className="ata-card__objeto">{realcar(ata.objeto, termo)}</div>
                    <div className="ata-card__org">
                        <span>{ata.orgao}</span>
                        <span className="lg-badge lg-badge--info">{ESFERA_LABEL[ata.esfera_codigo] || ata.esfera}</span>
                        <span style={{color: 'var(--text-tertiary)'}}>· {ata.uf} · Ata {ata.numero_ata || '—'} · {ata.modalidade}</span>
                    </div>
                </div>
                <span className={`selo ${selo.cls}`}>{selo.label}</span>
            </div>

            <div className="ata-card__meta">
                <div className="meta-item">
                    <div className="meta-item__label">Vigência</div>
                    <div className="meta-item__value">{fmtDate(ata.vigencia_inicio)} – {fmtDate(ata.vigencia_fim)}</div>
                </div>
                <div className="meta-item">
                    <div className="meta-item__label">Status</div>
                    <div className="meta-item__value">{ata.status_vigencia === 'A_VENCER' ? 'A vencer' : ata.status_vigencia === 'VIGENTE' ? 'Vigente' : ata.status_vigencia === 'VENCIDA' ? 'Vencida' : 'Cancelada'}</div>
                </div>
                <div className="meta-item">
                    <div className="meta-item__label">Aderência</div>
                    <div className="meta-item__value">{score}%</div>
                </div>
                {ata.preco_item != null && (
                    <div className="meta-item">
                        <div className="meta-item__label">Item da busca (unit.)</div>
                        <div className="meta-item__value">{brl(ata.preco_item)}</div>
                    </div>
                )}
                <div className="meta-item" style={{display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 4}}>
                    {ata.link_pncp && (
                        <a className="icon-link" href={ata.link_pncp} target="_blank" rel="noopener noreferrer">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                            Abrir no PNCP
                        </a>
                    )}
                    <span className="icon-link" onClick={() => copiar(ata.numero_controle_pncp)}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                        Copiar nº de controle
                    </span>
                </div>
            </div>

            {ata.alertas.length > 0 && (
                <div className="ata-card__alerts">
                    {ata.alertas.map((a, i) => {
                        const chip = alertChip(a)
                        return (
                            <span key={i} className={`alert-chip ${chip.cls}`}>
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" dangerouslySetInnerHTML={{__html: ALERT_ICONS[chip.icon]}} />
                                {chip.txt}
                            </span>
                        )
                    })}
                </div>
            )}

            <div className="ata-card__actions">
                <div className="match-score">
                    <span>Aderência</span>
                    <div className="match-bar"><div className="match-bar__fill" style={{width: `${score}%`}}></div></div>
                    <strong style={{color: 'var(--text-secondary)'}}>{score}%</strong>
                </div>
                <div style={{display: 'flex', gap: 10}}>
                    <button className="lg-btn lg-btn--secondary lg-btn--sm" onClick={() => onAbrir(ata.numero_controle_pncp)}>Ver detalhes</button>
                    <button className="lg-btn lg-btn--brand lg-btn--sm" disabled={!podeAvaliar} onClick={() => onAvaliar(ata.numero_controle_pncp)}>
                        {podeAvaliar ? 'Avaliar adesão' : (vedada ? 'Adesão vedada' : 'Fora de vigência')}
                    </button>
                </div>
            </div>
        </div>
    )
}

export default function ResultadosList(
    {resultados, origem, termo, loading, erro, sugestoes, sugestoesPendentes, irps,
     onRefinar, onSugestao, onAbrir, onAvaliar}: Props
) {
    const [ordenar, setOrdenar] = useState<Ordenar>('aderencia')
    const [filtro, setFiltro] = useState<Filtro>('todas')

    // Espera das sugestões de termo: 0 atas + push da IA pendente. Enquanto isso a
    // tela NÃO afirma "nenhuma ata encontrada" (o vazio viraria sugestão logo depois).
    const esperandoSugestoes = !loading && !erro && resultados.length === 0 && sugestoesPendentes

    let vistos = filtro === 'todas' ? [...resultados] : resultados.filter((a) => a.selo === filtro)
    // "Aderência" = a ORDEM DO SERVIDOR: faixa de aderência → menor preço do item
    // casado (desempate pedido pelo fundador) → vigência/esfera. Reordenar por
    // score aqui desfazia o desempate por preço.
    if (ordenar === 'vigencia') {
        vistos = [...vistos].sort((a, b) => (b.vigencia_fim || '').localeCompare(a.vigencia_fim || ''))
    }

    return (
        <div>
            <header className="page-header">
                <div className="crumb">
                    <a onClick={onRefinar}>Ata Inteligente</a>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                    <span>Resultados</span>
                </div>
                <div className="header-row">
                    <h1 className="lg-h2">Atas candidatas para "{termo}"</h1>
                    <button className="lg-btn lg-btn--secondary lg-btn--sm" onClick={onRefinar}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
                        Refinar busca
                    </button>
                </div>
                <p className="lg-text-muted" style={{marginBottom: 0}}>
                    {loading
                        ? 'Consultando atas no PNCP…'
                        : esperandoSugestoes
                        ? 'Nenhuma ata direta — buscando termos alternativos com a Licito.Guru IA…'
                        : <>{vistos.length}{vistos.length !== resultados.length ? ` de ${resultados.length}` : ''} ata(s) · <strong>vigentes, adesão permitida ou a confirmar</strong></>}
                    {!loading && origem === 'pncp' && (
                        <strong style={{color: 'var(--feedback-success-strong)'}}> · fonte: PNCP ao vivo</strong>
                    )}
                </p>
            </header>

            <div className="page-content">
                <IrpSection irps={irps} termo={termo} />

                {!loading && resultados.length > 0 && (
                    <div className="section-divider" style={{marginTop: irps.length ? 8 : 0}}>
                        <span className="section-divider__label">Atas vigentes para adesão (carona)</span>
                        <div className="section-divider__line"></div>
                    </div>
                )}

                {!loading && !erro && resultados.length > 0 && (
                    <div className="res-toolbar">
                        <span className="res-toolbar__label">Ordenar</span>
                        <select className="lg-select" value={ordenar} onChange={(e) => setOrdenar(e.target.value as Ordenar)}>
                            <option value="aderencia">Maior aderência</option>
                            <option value="vigencia">Vigência mais longa</option>
                        </select>
                        <span className="res-toolbar__label">Selo</span>
                        <select className="lg-select" value={filtro} onChange={(e) => setFiltro(e.target.value as Filtro)}>
                            <option value="todas">Todos</option>
                            <option value="ADESAO_PERMITIDA">Adesão permitida</option>
                            <option value="ADESAO_CONDICIONAL">A confirmar</option>
                        </select>
                    </div>
                )}

                <div style={{display: 'flex', flexDirection: 'column', gap: 16}}>
                    {loading && <DicasLoading titulo="Buscando atas no PNCP…" />}

                    {!loading && erro && (
                        <div className="lg-empty">
                            <svg className="lg-empty__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
                            <div className="lg-empty__title">Não foi possível buscar</div>
                            <div className="lg-empty__description">{erro}</div>
                            <SugestoesChips sugestoes={sugestoes} onSugestao={onSugestao} />
                        </div>
                    )}

                    {esperandoSugestoes && (
                        <DicasLoading titulo="Buscando termos melhores com a Licito.Guru IA…" />
                    )}

                    {!loading && !erro && !esperandoSugestoes && resultados.length === 0 && (
                        <div className="lg-empty">
                            <svg className="lg-empty__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
                            <div className="lg-empty__title">Nenhuma ata encontrada para "{termo}"</div>
                            <div className="lg-empty__description">O PNCP licita por termos genéricos: tente "smartphone" no lugar de "iphone", ou amplie os filtros.</div>
                            <SugestoesChips sugestoes={sugestoes} onSugestao={onSugestao} />
                        </div>
                    )}

                    {!loading && !erro && vistos.map((ata) => (
                        <AtaCard key={ata.numero_controle_pncp} ata={ata} termo={termo} onAbrir={onAbrir} onAvaliar={onAvaliar} />
                    ))}
                </div>
            </div>
        </div>
    )
}
